import { useEffect, Suspense, useState } from 'react'
import { useOktaAuth } from '@okta/okta-react'
import { Switch } from 'react-router-dom'
import Routes from '../Routes/Routes.react'
import { useAppDispatch, useAppState } from '../Context/AppProvider'
import { useLDClient } from 'launchdarkly-react-client-sdk'
import ThreeDotLoader from '../Components/Commons/Loaders/ThreeDotLoader.react'
import { useLocation } from 'react-router'
import { LoginCallback } from '@okta/okta-react'
import { identify as identifyFullStoryUser } from '@fullstory/browser'
import { useQuery } from 'react-query'
import SiteHeader from '../Components/SiteHeader'
import useFetchTenantId from '../Utilities/API/users/fetchTenantId'

const BackOffice: React.FC = () => {
  const dispatch = useAppDispatch()
  const appState = useAppState()
  const { authState, oktaAuth } = useOktaAuth()
  const ldClient = useLDClient()
  const { pathname = '' } = useLocation()
  const [launchDarklyReady, setLaunchDarklyReady] = useState(false)
  const fetchTenantId = useFetchTenantId()
  const {
    data: tenant,
    isFetched,
    refetch,
  } = useQuery('tenantId', () => fetchTenantId(), { enabled: false, retry: 1 })

  useEffect(() => {
    if (!authState) return

    const dispatchData = (tenantId: string | undefined) => {
      dispatch({
        type: 'SET_USER_INFO',
        user: {
          name: authState.idToken?.claims.name ?? '',
          email: authState.idToken?.claims.email ?? '',
        },
        permissions: authState.accessToken?.claims.groups,
        role: authState.accessToken?.claims.role,
        tenantId,
      })
    }

    const getMetaData = () => {
      if (!isFetched) {
        refetch().then((response) => {
          dispatchData(response.data?.tenantId)
        })
      } else {
        dispatchData(tenant?.tenantId)
      }
    }

    const setDarklyUser = async () => {
      const ldUser = {
        key: authState.accessToken?.claims?.uid,
        email: authState.idToken?.claims?.email,
        custom: {
          tenantId: authState.accessToken?.claims.tenantId,
        },
      }

      await ldClient?.waitUntilReady()
      await ldClient?.identify(ldUser)
      setLaunchDarklyReady(true)
    }

    const setFullStoryUser = () => {
      const { uid } = authState.accessToken?.claims || { uid: undefined }
      const { name, email } = authState.idToken?.claims || {}
      if (uid) {
        identifyFullStoryUser(uid, {
          displayName: name,
          email,
        })
      }
    }

    if (authState.isAuthenticated) {
      getMetaData()
      setDarklyUser()
      setFullStoryUser()
    } else {
      oktaAuth.signInWithRedirect()
    }

    // eslint-disable-next-line
  }, [authState])

  // Okta requires a redirect route to be available
  if (pathname.includes('/login/callback')) {
    return <LoginCallback />
  }

  if (!authState || !appState.role || !launchDarklyReady) {
    return (
      <div className="center-align">
        <ThreeDotLoader />
      </div>
    )
  }

  return (
    <>
      <SiteHeader
        username={appState.user.name}
        permissions={appState.permissions}
      />
      <Switch>
        <Suspense fallback={<span></span>}>
          <Routes />
        </Suspense>
      </Switch>
    </>
  )
}

export default BackOffice
